/**
 * Hover price preview for product-variant-picker-diamond.
 * Hovering an option shows that prospective variant price; leaving restores.
 * Always adds the selected Production Speed product price on top of the variant price.
 */
(() => {
  function moneyFormat() {
    return (
      window.theme?.settings?.money_with_currency_format ||
      window.theme?.settings?.money_format ||
      '${{amount}}'
    );
  }

  function formatCents(cents, format) {
    if (typeof formatMoney === 'function') {
      return formatMoney(cents, format || moneyFormat());
    }
    const amount = (Number(cents) / 100).toFixed(2);
    return `$${amount}`;
  }

  function getVariantSelects(sectionId) {
    return (
      document.querySelector(`#variant-selects-${sectionId}`) ||
      document.querySelector(`variant-selects.product-variant-picker-diamond[data-section="${sectionId}"]`) ||
      document.querySelector('variant-selects.product-variant-picker-diamond')
    );
  }

  function getProductionSpeedRoot(sectionId) {
    if (!sectionId) {
      return document.querySelector('product-production-speed');
    }
    return (
      document.querySelector(`product-production-speed[data-section-id="${sectionId}"]`) ||
      document.querySelector('product-production-speed')
    );
  }

  /** Selected Production Speed product add-on (cents). */
  function getProductionSpeedAddon(sectionId) {
    const root = getProductionSpeedRoot(sectionId);
    if (!root) return { price: 0, compare: 0 };
    const selected = root.querySelector('.product-production-speed__radio:checked');
    if (!selected) return { price: 0, compare: 0 };
    return {
      price: Number(selected.dataset.ppsPrice) || 0,
      compare: Number(selected.dataset.ppsComparePrice) || 0,
    };
  }

  function sumVariantWithAddon(variant, addon) {
    const basePrice = Number(variant?.price) || 0;
    const baseCompare = Number(variant?.compare_at_price) || 0;
    const addPrice = Number(addon?.price) || 0;
    const addCompare = Number(addon?.compare) || 0;

    const price = basePrice + addPrice;
    const onSaleBase = baseCompare > basePrice;
    const onSaleAdd = addCompare > addPrice;
    const compareBase = onSaleBase ? baseCompare : basePrice;
    const compareAdd = onSaleAdd ? addCompare : addPrice;
    const compare = onSaleBase || onSaleAdd ? compareBase + compareAdd : 0;

    return { price, compare };
  }

  function getVariants(root) {
    const script = root.querySelector('script[type="application/json"]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  function getFieldsets(root) {
    return [...root.querySelectorAll(':scope > fieldset.product-form__input')];
  }

  function getCommittedOptions(root) {
    return getFieldsets(root).map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  function findVariant(variants, options) {
    if (!options.length || options.some((o) => o == null)) return null;
    return (
      variants.find((variant) =>
        (variant.options || []).every((opt, index) => opt === options[index])
      ) || null
    );
  }

  function optionIndexForFieldset(root, fieldset) {
    return getFieldsets(root).indexOf(fieldset);
  }

  function resolveHoverValue(target, fieldset) {
    const caratLabel = target.closest('.product-diamond-options__weight-item');
    if (caratLabel) {
      const id = caratLabel.getAttribute('for');
      const input = id ? fieldset.querySelector(`#${CSS.escape(id)}`) : null;
      return input?.value || null;
    }

    const purityLabel = target.closest(
      '.product-variant-picker-diamond__purity .product-diamond-options__quality-strip > label, .product-variant-picker-diamond__quality .product-diamond-options__quality-strip > label'
    );
    if (purityLabel) {
      const id = purityLabel.getAttribute('for');
      const input = id ? fieldset.querySelector(`#${CSS.escape(id)}`) : null;
      return input?.value || null;
    }

    // Generic radio label fallback (product-variant-options swatches)
    const label = target.closest('label[for]');
    if (label) {
      const input = fieldset.querySelector(`#${CSS.escape(label.htmlFor)}`);
      return input?.value || null;
    }

    return null;
  }

  function paintPrice(priceEl, variant) {
    if (!priceEl || !variant) return;
    const format = priceEl.dataset.moneyFormat || moneyFormat();
    const saleEl = priceEl.querySelector('[data-pdo-sale-price]');
    const compareEl = priceEl.querySelector('[data-pdo-compare-price]');
    const badgeEl = priceEl.querySelector('[data-pdo-save-badge]');

    const addon = getProductionSpeedAddon(priceEl.dataset.section);
    const summed = sumVariantWithAddon(variant, addon);

    if (saleEl) saleEl.textContent = formatCents(summed.price, format);

    const onSale = summed.compare > summed.price;
    if (compareEl) {
      if (onSale) {
        compareEl.hidden = false;
        compareEl.textContent = formatCents(summed.compare, format);
      } else {
        compareEl.hidden = true;
      }
    }

    if (badgeEl) {
      if (onSale) {
        badgeEl.hidden = false;
        const pct = Math.round(((summed.compare - summed.price) * 100) / summed.compare);
        if (!badgeEl.dataset.fixedLabel) {
          badgeEl.textContent = `Save ${pct}%`;
        }
      } else {
        badgeEl.hidden = true;
      }
    }
  }

  function paintCommitted(priceEl, root) {
    const variants = getVariants(root);
    const options = getCommittedOptions(root);
    const variant = findVariant(variants, options);
    if (variant) {
      // Keep dataset as base variant only so Production Speed can add on top
      priceEl.dataset.price = String(variant.price);
      priceEl.dataset.comparePrice = String(variant.compare_at_price || 0);
      paintPrice(priceEl, variant);
    }
    priceEl.classList.remove('is-previewing');
  }

  function initPriceBlock(priceEl) {
    if (priceEl.dataset.priceHoverReady === 'true') return;
    priceEl.dataset.priceHoverReady = 'true';

    const sectionId = priceEl.dataset.section;
    const root = getVariantSelects(sectionId);
    if (!root) return;

    const onEnter = (event) => {
      const fieldset = event.target.closest('fieldset.product-form__input');
      if (!fieldset || !root.contains(fieldset)) return;

      const hoverValue = resolveHoverValue(event.target, fieldset);
      if (!hoverValue) return;

      const index = optionIndexForFieldset(root, fieldset);
      if (index < 0) return;

      const options = getCommittedOptions(root);
      options[index] = hoverValue;

      const variant = findVariant(getVariants(root), options);
      if (!variant) return;

      // Preview only — do not overwrite base dataset.price
      paintPrice(priceEl, variant);
      priceEl.classList.add('is-previewing');
    };

    const onLeave = (event) => {
      const related = event.relatedTarget;
      if (related && root.contains(related)) return;
      paintCommitted(priceEl, root);
    };

    root.addEventListener('mouseover', onEnter);
    root.addEventListener('mouseleave', onLeave);
    root.addEventListener('change', () => paintCommitted(priceEl, root));

    // Initial / post-swap paint must include Production Speed add-on
    paintCommitted(priceEl, root);
  }

  function boot() {
    document.querySelectorAll('[data-price-diamond]').forEach(initPriceBlock);
  }

  /**
   * VariantSelects.renderProductInfo() replaces #price-* innerHTML with
   * server HTML (variant only). Rebind + re-sum Production Speed after that.
   */
  function onVariantChange(event) {
    const sectionId = event?.data?.sectionId;
    if (!sectionId) return;
    boot();
    const priceEl =
      document.querySelector(`#price-${sectionId} [data-price-diamond]`) ||
      document.querySelector(`[data-price-diamond][data-section="${sectionId}"]`);
    const root = getVariantSelects(sectionId);
    if (priceEl && root) paintCommitted(priceEl, root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  // After variant section HTML swap, rebind
  const observer = new MutationObserver(() => boot());
  observer.observe(document.body, { childList: true, subtree: true });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  }

  // Expose for Production Speed to re-paint after its own selection changes
  window.PriceDiamond = window.PriceDiamond || {};
  window.PriceDiamond.repaintSection = function (sectionId) {
    const priceEl =
      document.querySelector(`#price-${sectionId} [data-price-diamond]`) ||
      document.querySelector(`[data-price-diamond][data-section="${sectionId}"]`);
    const root = getVariantSelects(sectionId);
    if (priceEl && root) paintCommitted(priceEl, root);
  };
})();
